import React from 'react';

import {
  Grid,
  Stack,
  Typography,
  Chip,
  Divider,
  Tooltip,
  makeStyles,
} from '@gogame/components';
import { FormField, withForm, SidePageV2 } from '@gogame/form';

import { validationSchema } from './schemas';
import {
  NETWORKS,
  CHANNELS,
  EXCLUSIVE_FOR_AFFILIATES,
  LANGUAGE,
  ENHANCERS,
  TAGS,
  JIRA_TASK,
  TESTED,
  REQUESTED_BY,
  POSTFIX,
  OUTSOURCE_STUDIO_ID,
  DESIGNER,
  DESCRIPTION,
  AI_GENERATED,
  PRODUCTION_TYPE,
} from './constants';
import {
  loadChannels,
  loadLanguages,
  loadEnhancers,
  loadTags,
  loadOutsourceStudios,
  loadAuthors,
  loadNetworks,
  loadExclusiveForAffiliates,
  loadTested,
  loadRequestedBy,
  loadProductionTypes,
} from 'scenes/Gallery/GalleryPage/Creatives/sources/fields';
import SelectedPreviewGrid from 'scenes/Gallery/components/SelectedPreviewGrid';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

const useStyles = makeStyles({
  chips: {
    margin: -4,
    '& > * ': {
      display: 'inline-flex',
      margin: 4,
    },
  },
});

const defaultValues = validationSchema.default();

const defaultNormalizer = (v) => v;

const BulkEditForm = withForm({
  validationSchema: validationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  selectedRows,
  values,
  setFieldValue,
  loading,
  onClose,
  onExited,
  open,
}) => {
  const {
    isAllowExclusiveForAffiliates,
    isAllowJiraTask,
    isAllowRequestedBy,
    isAllowOutsourceStudio,
    isAllowDesigner,
    isAllowProductionType,
  } = useGalleryPermissions();

  const classes = useStyles();

  const isFieldSelected = (fieldName) => values[fieldName] !== undefined;

  const networksSelected = isFieldSelected(NETWORKS);
  const businessPartnerSelected = isFieldSelected(CHANNELS);
  const exclusiveForAffiliatesSelected = isFieldSelected(
    EXCLUSIVE_FOR_AFFILIATES
  );
  const languageSelected = isFieldSelected(LANGUAGE);
  const enhancerSelected = isFieldSelected(ENHANCERS);
  const aiGeneratedSelected = isFieldSelected(AI_GENERATED);
  const tagSelected = isFieldSelected(TAGS);
  const jiraTaskSelected = isFieldSelected(JIRA_TASK);
  const testedSelected = isFieldSelected(TESTED);
  const requestedBySelected = isFieldSelected(REQUESTED_BY);
  const postfixSelected = isFieldSelected(POSTFIX);
  const outsourceStudioSelected = isFieldSelected(OUTSOURCE_STUDIO_ID);
  const designerStudioSelected = isFieldSelected(DESIGNER);
  const descriptionSelected = isFieldSelected(DESCRIPTION);
  const productionTypeSelected = isFieldSelected(PRODUCTION_TYPE);

  const selectedSome = Object.keys(values).length > 1;

  const handleSelectField = (id) => {
    setFieldValue(id, isFieldSelected(id) ? undefined : defaultValues[id]);
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'md'}
    >
      <SidePageV2.Title title={title} />
      <SidePageV2.Content>
        <Stack spacing={3} height={'100%'}>
          <Stack spacing={1}>
            <Typography color={'textSecondary'}>
              Select fields to edit
            </Typography>
            <div className={classes.chips}>
              <Chip
                label={'Networks'}
                checked={networksSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(NETWORKS)}
                size={'small'}
              />
              <Chip
                label={'Marketing partner'}
                checked={businessPartnerSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(CHANNELS)}
                size={'small'}
              />
              {isAllowExclusiveForAffiliates ? (
                <Chip
                  label={'Exclusive for affiliates'}
                  checked={exclusiveForAffiliatesSelected}
                  variant={'outlined'}
                  onClick={() => handleSelectField(EXCLUSIVE_FOR_AFFILIATES)}
                  size={'small'}
                />
              ) : null}
              <Chip
                label={'Language'}
                checked={languageSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(LANGUAGE)}
                size={'small'}
              />
              {isAllowProductionType ? (
                <Chip
                  label={'Production type'}
                  checked={productionTypeSelected}
                  variant={'outlined'}
                  onClick={() => handleSelectField(PRODUCTION_TYPE)}
                  size={'small'}
                />
              ) : null}
              <Chip
                label={'Enhancers'}
                checked={enhancerSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(ENHANCERS)}
                size={'small'}
              />
              <Chip
                label={'AI Generated'}
                checked={aiGeneratedSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(AI_GENERATED)}
                size={'small'}
              />
              <Chip
                label={'Internal tags'}
                checked={tagSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(TAGS)}
                size={'small'}
              />
              {isAllowJiraTask ? (
                <Chip
                  label={'Jira task URL'}
                  checked={jiraTaskSelected}
                  variant={'outlined'}
                  onClick={() => handleSelectField(JIRA_TASK)}
                  size={'small'}
                />
              ) : null}
              <Chip
                label={'Tested'}
                checked={testedSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(TESTED)}
                size={'small'}
              />
              {isAllowRequestedBy ? (
                <Chip
                  label={'Requested by'}
                  checked={requestedBySelected}
                  variant={'outlined'}
                  onClick={() => handleSelectField(REQUESTED_BY)}
                  size={'small'}
                />
              ) : null}
              <Chip
                label={"Concept's name postfix"}
                checked={postfixSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(POSTFIX)}
                size={'small'}
              />
              {isAllowOutsourceStudio ? (
                <Tooltip
                  title={
                    designerStudioSelected
                      ? 'Unavailable when Designer is selected'
                      : null
                  }
                  wrapper
                >
                  <Chip
                    label={'Outsource studio'}
                    checked={outsourceStudioSelected}
                    variant={'outlined'}
                    onClick={() => handleSelectField(OUTSOURCE_STUDIO_ID)}
                    disabled={designerStudioSelected}
                    size={'small'}
                  />
                </Tooltip>
              ) : null}
              {isAllowDesigner ? (
                <Tooltip
                  title={
                    outsourceStudioSelected
                      ? 'Unavailable when Outsource studio is selected'
                      : null
                  }
                  wrapper
                >
                  <Chip
                    label={'Designer'}
                    checked={designerStudioSelected}
                    variant={'outlined'}
                    onClick={() => handleSelectField(DESIGNER)}
                    disabled={outsourceStudioSelected}
                    size={'small'}
                  />
                </Tooltip>
              ) : null}
              <Chip
                label={'Description'}
                checked={descriptionSelected}
                variant={'outlined'}
                onClick={() => handleSelectField(DESCRIPTION)}
                size={'small'}
              />
            </div>
          </Stack>
          {selectedSome ? (
            <Stack>
              <Grid container spacing={3}>
                {networksSelected ? (
                  <Grid item xs={6}>
                    <FormField.MultiSelect
                      label={'Networks'}
                      name={NETWORKS}
                      source={loadNetworks}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {businessPartnerSelected ? (
                  <Grid item xs={6}>
                    <FormField.MultiSelect
                      label={'Marketing partners'}
                      name={CHANNELS}
                      source={loadChannels}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {exclusiveForAffiliatesSelected ? (
                  <Grid item xs={6}>
                    <FormField.MultiSelect
                      label={'Exclusive for affiliates'}
                      name={EXCLUSIVE_FOR_AFFILIATES}
                      source={loadExclusiveForAffiliates}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {languageSelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Language'}
                      name={LANGUAGE}
                      source={loadLanguages}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {isAllowProductionType && productionTypeSelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Production type'}
                      name={PRODUCTION_TYPE}
                      source={loadProductionTypes}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {enhancerSelected ? (
                  <Grid item xs={6}>
                    <FormField.MultiSelect
                      label={'Enhancers'}
                      name={ENHANCERS}
                      source={loadEnhancers}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {aiGeneratedSelected ? (
                  <Grid item xs={6}>
                    <FormField.Checkbox
                      label={'AI Generated'}
                      name={AI_GENERATED}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {tagSelected ? (
                  <Grid item xs={6}>
                    <FormField.MultiSelect
                      label={'Internal tags'}
                      name={TAGS}
                      source={loadTags}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {jiraTaskSelected ? (
                  <Grid item xs={6}>
                    <FormField.TextField
                      label={'Jira task URL'}
                      name={JIRA_TASK}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {testedSelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Tested'}
                      name={TESTED}
                      source={loadTested}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {requestedBySelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Requested by'}
                      name={REQUESTED_BY}
                      source={loadRequestedBy}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {postfixSelected ? (
                  <Grid item xs={6}>
                    <FormField.TextField
                      label={"Concept's name postfix"}
                      name={POSTFIX}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {isAllowOutsourceStudio && outsourceStudioSelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Outsource studio'}
                      name={OUTSOURCE_STUDIO_ID}
                      source={loadOutsourceStudios}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {isAllowDesigner && designerStudioSelected ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Designer'}
                      name={DESIGNER}
                      source={loadAuthors}
                      normalizeValue={defaultNormalizer}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                {descriptionSelected ? (
                  <Grid item xs={12}>
                    <FormField.TextField
                      label={'Description'}
                      name={DESCRIPTION}
                      multiline={true}
                      padding={0}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Stack>
          ) : null}
          <Divider />
          <Stack overflow={'auto'}>
            <SelectedPreviewGrid selectedRows={selectedRows} />
          </Stack>
        </Stack>
      </SidePageV2.Content>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default BulkEditForm;
